/**
 * SmartQueue - Unified Priority Action Feed
 *
 * "Tinder for Sales" UI with multi-dimensional priority scoring.
 * Swipe right to execute, left to dismiss, tap for details.
 *
 * Priority = Intent(30%) + Fit(25%) + Stage(25%) + Urgency(20%)
 *
 * Created: 2026-01-05
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card } from '@/components/UI/Card/Card';
import { Button } from '@/components/UI/Button/Button';
import { DiamondIcon, GoldText } from '@/components/UI';
import { useToast } from '@/contexts/ToastContext';
import { useSmartQueueStore } from '@/stores';
import { useSmartQueueWebSocket } from '@/hooks/useSmartQueueWebSocket';
import { UI, QUEUE, getUrgencyInfo, getStageInfo } from '@/config/constants';
import type { QueueItem, QueueFilters } from '@/types/queue';
import {
  Flame,
  Snowflake,
  ChevronRight,
  ChevronLeft,
  Clock,
  Building,
  Briefcase,
  Mail,
  Send,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Zap,
  Users,
  Target,
  RefreshCw,
} from 'lucide-react';
import { HeatThermometer } from '@/components/TAG/HeatThermometer';
import { OutreachCompose } from '@/components/Outreach/OutreachCompose';
import { dealBriefingApi, type BriefingData } from '@/services/dealBriefingApi';

interface SmartQueueProps {
  initialFilters?: QueueFilters;
  /** True when the tenant has no accounts/prospects yet (cold-start) — the empty queue is
   *  an "activate" moment, not a "you cleared your work" moment. Distinguishes the two so
   *  we never congratulate a brand-new user for work they haven't started. */
  coldStart?: boolean;
}

export const SmartQueue: React.FC<SmartQueueProps> = ({ initialFilters, coldStart }) => {
  const navigate = useNavigate();
  const { addToast } = useToast();

  // Store state and actions
  const items = useSmartQueueStore(state => state.items);
  const currentIndex = useSmartQueueStore(state => state.currentIndex);
  const isLoading = useSmartQueueStore(state => state.isLoading);
  const isExecuting = useSmartQueueStore(state => state.isExecuting);
  const error = useSmartQueueStore(state => state.error);
  const fetchQueue = useSmartQueueStore(state => state.fetchQueue);
  const executeAction = useSmartQueueStore(state => state.executeAction);
  const dismissAction = useSmartQueueStore(state => state.dismissAction);
  const snoozeAction = useSmartQueueStore(state => state.snoozeAction);

  // Compose modal state — must be at top level (React hooks rule)
  const [composeTarget, setComposeTarget] = useState<QueueItem | null>(null);
  const [sentConfirmation, setSentConfirmation] = useState<string | null>(null);

  // Deal briefing context for current card
  const [briefingContext, setBriefingContext] = useState<BriefingData | null>(null);

  // Fetch briefing for current prospect's company
  const currentItem = items[currentIndex];
  useEffect(() => {
    setBriefingContext(null);
    if (!currentItem?.prospect_company) return;
    dealBriefingApi.list(1, 5)
      .then(res => {
        const match = res.items.find(b =>
          b.company_name?.toLowerCase() === currentItem.prospect_company?.toLowerCase()
        );
        if (match) {
          dealBriefingApi.get(match.id)
            .then(detail => setBriefingContext(detail.briefing_data))
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [currentItem?.prospect_id]);

  // Real-time WebSocket updates
  const { isConnected } = useSmartQueueWebSocket({
    enabled: true,
    onNewItem: (item) => {
      addToast({
        type: 'info',
        title: 'New Action',
        message: `New priority action: ${item.prospect_name}`,
      });
    },
    onItemUpdated: () => {
      // Silent update - store handles it
    },
    onItemRemoved: () => {
      // Silent removal - store handles it
    },
    onRefresh: () => {
      addToast({
        type: 'info',
        title: 'Queue Updated',
        message: 'Queue refreshed with new priorities',
      });
    },
  });

  // Swipe gesture state
  const [startX, setStartX] = useState(0);
  const [offsetX, setOffsetX] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const remainingItems = items.length - currentIndex;

  // Fetch queue on mount
  useEffect(() => {
    fetchQueue(initialFilters);
  }, [fetchQueue, initialFilters]);

  // Swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    setStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const currentX = e.touches[0].clientX;
    const diff = currentX - startX;
    setOffsetX(diff);
  };

  const handleTouchEnd = () => {
    if (offsetX > UI.SWIPE_THRESHOLD) {
      handleSwipeRight();
    } else if (offsetX < -UI.SWIPE_THRESHOLD) {
      handleSwipeLeft();
    }
    setOffsetX(0);
  };

  // Swipe right: Execute action
  const handleSwipeRight = async () => {
    if (!currentItem || isExecuting) return;

    setSwipeDirection('right');

    try {
      const result = await executeAction(currentItem.id);
      addToast({
        type: 'success',
        title: 'Action Executed',
        message: result.message || `Action executed for ${currentItem.prospect_name}`,
      });
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to execute action',
      });
    } finally {
      setTimeout(() => setSwipeDirection(null), UI.TRANSITION_NORMAL);
    }
  };

  // Swipe left: Dismiss
  const handleSwipeLeft = async () => {
    if (!currentItem || isExecuting) return;

    setSwipeDirection('left');

    try {
      await dismissAction(currentItem.id);
      addToast({
        type: 'info',
        title: 'Skipped',
        message: 'Action skipped',
      });
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to dismiss action',
      });
    } finally {
      setTimeout(() => setSwipeDirection(null), UI.TRANSITION_NORMAL);
    }
  };

  // Snooze action
  const handleSnooze = async () => {
    if (!currentItem || isExecuting) return;

    try {
      const result = await snoozeAction(currentItem.id, { hours: 24 });
      addToast({
        type: 'info',
        title: 'Snoozed',
        message: result.message || 'Snoozed for 24 hours',
      });
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to snooze action',
      });
    }
  };

  // Get action icon and color based on type
  const getActionTypeInfo = (type: string) => {
    switch (type) {
      case QUEUE.ACTION_TYPES.HOT_LEAD:
        return { icon: <Flame className="w-5 h-5" />, color: 'text-red-500', bg: 'bg-red-500/10' };
      case QUEUE.ACTION_TYPES.QUALIFIED_LEAD:
        return { icon: <Target className="w-5 h-5" />, color: 'text-[var(--accent)]', bg: 'bg-[var(--accent-muted)]' };
      case QUEUE.ACTION_TYPES.FOLLOW_UP_DUE:
        return { icon: <Clock className="w-5 h-5" />, color: 'text-orange-500', bg: 'bg-orange-500/10' };
      case QUEUE.ACTION_TYPES.MEETING_REQUEST:
        return { icon: <Users className="w-5 h-5" />, color: 'text-[var(--state-success)]', bg: 'bg-[var(--state-success)]/10' };
      case QUEUE.ACTION_TYPES.STALE_PROSPECT:
        return { icon: <Snowflake className="w-5 h-5" />, color: 'text-[var(--text-muted)]', bg: 'bg-[var(--bg-hover)]' };
      default:
        return { icon: <Zap className="w-5 h-5" />, color: 'text-[var(--accent)]', bg: 'bg-[var(--accent-muted)]' };
    }
  };

  // Render priority score badge
  const renderPriorityBadge = (item: QueueItem) => {
    // Map priority (0-100) to a trend for the thermometer
    const trend = item.urgency_level === 'critical' ? 'surging' as const
      : item.urgency_level === 'high' ? 'rising' as const
      : item.urgency_level === 'medium' ? 'stable' as const
      : 'cold' as const;

    return <HeatThermometer score={item.priority} trend={trend} size="sm" />;
  };

  // Render "Why This Action" section
  const renderWhyThisAction = (item: QueueItem) => {
    if (!item.why_this_action || item.why_this_action.length === 0) return null;

    return (
      <div className="mt-4 p-3 bg-[var(--bg-elevated)] rounded-lg" data-testid="why-this-action">
        <div className="text-base font-medium text-[var(--text-secondary)] mb-2 flex items-center gap-2">
          <TrendingUp className="w-4 h-4" />
          Why this action?
        </div>
        <ul className="space-y-1">
          {item.why_this_action.map((reason, index) => (
            <li key={index} className="text-base text-[var(--text-muted)] flex items-start gap-2">
              <CheckCircle className="w-3 h-3 text-[var(--state-success)] mt-1 flex-shrink-0" />
              <span>{reason}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--accent)] mx-auto mb-4" />
          <p className="text-[var(--text-muted)]">Loading priority queue...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Card className="p-12 text-center bg-[var(--bg-card)] border-[var(--border)]">
        <AlertCircle className="w-16 h-16 text-[var(--state-danger)] mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-2">Error Loading Queue</h3>
        <p className="text-[var(--text-muted)] mb-6">{error}</p>
        <Button onClick={() => fetchQueue()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </Card>
    );
  }

  // Empty state — cold-start (no accounts yet) is an "activate" moment, NOT "all caught up".
  if (items.length === 0) {
    return coldStart ? (
      <Card className="p-12 text-center bg-[var(--bg-card)] border-[var(--border)]">
        <Target className="w-16 h-16 text-[var(--accent)] mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-2">Let’s fill your queue</h3>
        <p className="text-[var(--text-muted)] mb-6">
          Add accounts to track or import prospects, and Ricald will tell you who to contact today — with the reason.
        </p>
        <Button onClick={() => navigate('/app/accounts')}>
          <Target className="w-4 h-4 mr-2" />
          Add accounts
        </Button>
      </Card>
    ) : (
      <Card className="p-12 text-center bg-[var(--bg-card)] border-[var(--border)]">
        <CheckCircle className="w-16 h-16 text-[var(--state-success)] mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-2">All caught up!</h3>
        <p className="text-[var(--text-muted)] mb-6">
          You’ve worked through today’s priority queue — nice work. New signals will surface here as they arrive.
        </p>
        <Button onClick={() => fetchQueue()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </Card>
    );
  }

  // End of queue
  if (!currentItem) {
    return (
      <Card className="p-12 text-center bg-[var(--bg-card)] border-[var(--border)]">
        <Flame className="w-16 h-16 text-[var(--accent)] mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-2">Queue Complete!</h3>
        <p className="text-[var(--text-muted)] mb-6">
          You've reviewed all priority actions. Great work!
        </p>
        <Button onClick={() => fetchQueue()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Load More
        </Button>
      </Card>
    );
  }

  const actionTypeInfo = getActionTypeInfo(currentItem.action_type);
  const stageInfo = getStageInfo(currentItem.stage);
  const urgencyInfo = getUrgencyInfo(currentItem.urgency_level);

  return (
    <div className="min-h-screen bg-[var(--bg-canvas)]">
      {/* Luxury Header */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start justify-between mb-10"
        >
          <div>
            {/* Diamond Badge */}
            <div className="page-badge flex items-center gap-3 mb-4">
              <DiamondIcon size="sm" glow />
              <span>Action Center</span>
            </div>
            <h1 className="page-title">
              Priority{' '}
              <GoldText as="span" glow>
                Queue
              </GoldText>
            </h1>
            <p className="text-base mt-2" style={{ color: 'var(--text-muted)', fontWeight: 300 }}>
              {remainingItems} action{remainingItems !== 1 && 's'} ready for review
            </p>
          </div>
          {/* WebSocket connection indicator */}
          <div className="flex items-center gap-2 text-base">
            <span
              className={`w-2 h-2 rounded-full ${isConnected ? 'bg-[var(--state-success)]' : 'bg-[var(--text-muted)]'}`}
              title={isConnected ? 'Real-time updates active' : 'Connecting...'}
            />
            <span style={{ color: 'var(--text-muted)' }}>
              {isConnected ? 'Live' : 'Connecting'}
            </span>
          </div>
        </motion.div>
      </div>

      <div className="max-w-2xl mx-auto px-6 lg:px-8">

      {/* Send Confirmation (inline, not toast) */}
      {sentConfirmation && (
        <div
          className="flex items-center gap-2 p-3 rounded-lg mb-4 text-base font-medium"
          style={{
            background: 'color-mix(in srgb, var(--state-success) 15%, transparent)',
            color: 'var(--state-success)',
            border: '1px solid color-mix(in srgb, var(--state-success) 30%, transparent)',
          }}
        >
          <CheckCircle className="w-4 h-4" />
          Sent to {sentConfirmation}
        </div>
      )}

      {/* Action Card */}
      <div
        ref={cardRef}
        className={`relative transition-all duration-${UI.TRANSITION_NORMAL} ${
          swipeDirection === 'right'
            ? 'transform translate-x-96 opacity-0'
            : swipeDirection === 'left'
            ? 'transform -translate-x-96 opacity-0'
            : ''
        }`}
        style={{
          transform: offsetX !== 0 ? `translateX(${offsetX}px) rotate(${offsetX / 20}deg)` : undefined,
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        data-testid={`queue-card-${currentItem.id}`}
      >
        <Card className="p-6 shadow-lg">
          {/* Card Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              {/* Action Type Badge */}
              <div className={`p-2 rounded-lg ${actionTypeInfo.bg}`}>
                <span className={actionTypeInfo.color}>{actionTypeInfo.icon}</span>
              </div>
              <div>
                <div className="text-base text-[var(--text-muted)]">
                  {QUEUE.ACTION_TYPE_LABELS[currentItem.action_type as keyof typeof QUEUE.ACTION_TYPE_LABELS]}
                </div>
                <h3 className="text-lg font-semibold">{currentItem.prospect_name}</h3>
              </div>
            </div>

            {/* Priority Badge */}
            <div data-testid="priority-badge">
              {renderPriorityBadge(currentItem)}
            </div>
          </div>

          {/* Prospect Details */}
          <div className="space-y-2 mb-4">
            {currentItem.prospect_title && (
              <div className="flex items-center gap-2 text-[var(--text-muted)]">
                <Briefcase className="w-4 h-4" />
                <span>{currentItem.prospect_title}</span>
              </div>
            )}
            {currentItem.prospect_company && (
              <div className="flex items-center gap-2 text-[var(--text-muted)]">
                <Building className="w-4 h-4" />
                <span>{currentItem.prospect_company}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-[var(--text-muted)]">
              <Mail className="w-4 h-4" />
              <span>{currentItem.prospect_email}</span>
            </div>
          </div>

          {/* Stage & Urgency Badges */}
          <div className="flex gap-2 mb-4">
            <span className={`px-2 py-1 rounded text-xs font-medium ${stageInfo.className}`}>
              {stageInfo.label} Stage
            </span>
            <span className={`px-2 py-1 rounded text-xs font-medium ${urgencyInfo.className}`}>
              {urgencyInfo.label} Urgency
            </span>
          </div>

          {/* Briefing Context (replaces score breakdown when available) */}
          {briefingContext ? (
            <div className="mt-4 p-3 rounded-lg" style={{ background: 'var(--bg-elevated)' }}>
              <div className="text-base font-medium mb-2 flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
                <TrendingUp className="w-4 h-4" />
                Why now
              </div>
              <ul className="space-y-1.5 text-base" style={{ color: 'var(--text-muted)' }}>
                {briefingContext.recommended_action && (
                  <li className="flex items-start gap-2">
                    <Zap className="w-3 h-3 mt-1 flex-shrink-0" style={{ color: 'var(--accent)' }} />
                    <span style={{ color: 'var(--text-primary)' }}>{briefingContext.recommended_action}</span>
                  </li>
                )}
                {briefingContext.buying_committee && briefingContext.buying_committee.length > 0 && (
                  <li className="flex items-start gap-2">
                    <Users className="w-3 h-3 mt-1 flex-shrink-0" style={{ color: 'var(--state-info)' }} />
                    <span>{briefingContext.buying_committee.length} buying committee members engaged</span>
                  </li>
                )}
                {briefingContext.warm_paths && briefingContext.warm_paths.length > 0 && (
                  <li className="flex items-start gap-2">
                    <Target className="w-3 h-3 mt-1 flex-shrink-0" style={{ color: 'var(--state-success)' }} />
                    <span>Warm path: via {briefingContext.warm_paths[0].via}</span>
                  </li>
                )}
                {briefingContext.call_insights?.buying_signals && briefingContext.call_insights.buying_signals.length > 0 && (
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3 h-3 mt-1 flex-shrink-0" style={{ color: 'var(--state-success)' }} />
                    <span>Buying signal: {briefingContext.call_insights.buying_signals[0]}</span>
                  </li>
                )}
                {briefingContext.call_insights?.unresolved_objections && briefingContext.call_insights.unresolved_objections.length > 0 && (
                  <li className="flex items-start gap-2">
                    <AlertCircle className="w-3 h-3 mt-1 flex-shrink-0" style={{ color: 'var(--state-warning)' }} />
                    <span>Objection: {briefingContext.call_insights.unresolved_objections[0]}</span>
                  </li>
                )}
                {/* Fallback to why_this_action */}
                {currentItem.why_this_action?.map((reason, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle className="w-3 h-3 mt-1 flex-shrink-0" style={{ color: 'var(--state-success)' }} />
                    <span>{reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <>
              {/* Why This Action (score breakdown removed — undermines recommendations) */}
              {renderWhyThisAction(currentItem)}
            </>
          )}

          {/* Due Date */}
          {currentItem.due_at && (
            <div className="mt-4 pt-4 border-t border-[var(--border)] text-base text-[var(--text-muted)] flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>Due: {new Date(currentItem.due_at).toLocaleDateString()}</span>
            </div>
          )}
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 mt-6">
        <Button
          variant="outline"
          size="lg"
          className="flex-1"
          onClick={handleSwipeLeft}
          disabled={isExecuting}
          data-testid="action-skip"
        >
          <ChevronLeft className="w-5 h-5 mr-2" />
          Skip
        </Button>
        <Button
          variant="secondary"
          size="lg"
          onClick={handleSnooze}
          disabled={isExecuting}
          data-testid="action-snooze"
        >
          <Clock className="w-5 h-5" />
        </Button>
        <Button
          variant="outline"
          size="lg"
          onClick={() => setComposeTarget(currentItem)}
          disabled={isExecuting}
          data-testid="action-message"
        >
          <Send className="w-5 h-5" />
        </Button>
        <Button
          variant="primary"
          size="lg"
          className="flex-1"
          onClick={handleSwipeRight}
          disabled={isExecuting}
          data-testid="action-primary"
        >
          Done
          <ChevronRight className="w-5 h-5 ml-2" />
        </Button>
      </div>

      {/* Progress Indicator */}
      <div className="mt-6 text-center text-base text-[var(--text-muted)]">
        Action {currentIndex + 1} of {items.length}
      </div>
    </div>

    {/* Compose Modal */}
    <OutreachCompose
      isOpen={!!composeTarget}
      onClose={() => {
        const name = composeTarget?.prospect_name
        setComposeTarget(null)
        if (name) {
          setSentConfirmation(name)
          setTimeout(() => setSentConfirmation(null), 3000)
        }
      }}
      prospect={composeTarget ? {
        id: composeTarget.prospect_id,
        name: composeTarget.prospect_name,
        title: composeTarget.prospect_title || undefined,
        company: composeTarget.prospect_company || undefined,
        email: composeTarget.prospect_email || undefined,
      } : undefined}
      context={composeTarget?.why_this_action?.join('. ')}
    />
    </div>
  );
};

export default SmartQueue;
